import React from "react";
import { Box, Typography, Button, useTheme, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MeetingModal from "../../../MeetingModal";
import useGAEventsTracker from "../../../GAEventsHook";
import HeroImageOne from "../assets/Gemini_Generated_Image_uw931auw931auw93 (1) 1.png";
import HeroImageTwo from "../assets/Gemini_Generated_Image_nnmlc0nnmlc0nnml (1).png";
import HeroImageThree from "../assets/Tabloid - 83.png";

// Create motion components for MUI Box
const MotionBox = motion(Box);

/**
 * Hero Section for Smart Reporting Page
 * 
 * Features:
 * - Framer Motion animations
 * - 3-image collage layout
 * - Statistics section with icons
 * - Responsive design
 */
const HeroSection = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showMeeting, setShowMeeting] = React.useState(false);
  const GAEventsTracker = useGAEventsTracker("Smart Reports Activity");

  // Hero images - local assets
  const images = [HeroImageOne, HeroImageTwo, HeroImageThree];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const floatingVariants = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        padding: {
          xs: "170px 16px 40px 16px", // Mobile: Increased top padding to avoid navbar collision
          sm: "120px 24px 60px 24px", // Tablet: Increased top padding
          md: "80px 32px", // Desktop: Keep as is
        },
        position: "relative",
        overflow: "hidden",
        backgroundColor: "transparent",
        minHeight: { xs: "auto", sm: "auto" },
        maxHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          margin: "0 auto",
          width: "100%",
          padding: { 
            xs: "0 20px", 
            sm: "0 28px", 
            md: "0 40px", 
            lg: "0 56px",
            xl: "0 64px"
          },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: "40px", sm: "48px", md: "56px", lg: "64px" },
          alignItems: "center",
        }}
      >
        {/* Left Column: Text Content */}
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            textAlign: { xs: "center", md: "left" },
          }}
        >
          <MotionBox variants={itemVariants}>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px", lg: "48px" },
                fontWeight: 600,
                color: "#1e293b",
                lineHeight: 1.2,
                fontFamily: "'Poppins', sans-serif",
                marginBottom: { xs: "16px", md: "20px" },
                letterSpacing: "-0.01em",
              }}
            >
              Transform Complex Reports into{" "}
              <Box
                component="span"
                sx={{
                  color: "#3567D6",
                }}
              >
                Clear, Actionable Health Insights
              </Box>
            </Typography>
          </MotionBox>

          <MotionBox variants={itemVariants}>
            <Typography
              sx={{
                fontSize: { xs: "13px", sm: "14px", md: "15px" },
                color: "#475569",
                lineHeight: 1.6,
                marginBottom: { xs: "24px", md: "28px" },
                maxWidth: "550px",
                mx: { xs: "auto", md: 0 },
              }}
            >
              Turn complex medical data into clear, patient-friendly reports with smart templates and interactive insights.
            </Typography>
          </MotionBox>

          <MotionBox
            variants={itemVariants}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              justifyContent: { xs: "center", md: "flex-start" },
              width: "100%",
              marginBottom: { xs: "32px", md: "36px" },
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                GAEventsTracker("learn-more clicked", "learn-more clicked");

                // Smooth scroll to the bottom section of the Smart Reporting page
                if (typeof window !== "undefined") {
                  const bottomSection = document.getElementById("smart-report-bottom-section");
                  if (bottomSection && bottomSection.scrollIntoView) {
                    bottomSection.scrollIntoView({ behavior: "smooth", block: "start" });
                  } else {
                    window.scrollTo({
                      top: document.documentElement.scrollHeight,
                      behavior: "smooth",
                    });
                  }
                }
              }}
              sx={{
                background: "#022674",
                color: "white",
                fontSize: { xs: "13px", sm: "14px", md: "15px" },
                fontWeight: 600,
                fontFamily: "'Poppins', sans-serif",
                padding: { xs: "10px 24px", sm: "11px 28px", md: "12px 32px" },
                borderRadius: "8px",
                textTransform: "none",
                "&:hover": {
                  background: "#1E4DB7",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(2, 38, 116, 0.3)",
                },
              }}
            >
              Learn More
              <ArrowForwardIcon sx={{ marginLeft: "6px", fontSize: { xs: "18px", md: "20px" } }} />
            </Button>
          </MotionBox>
        </MotionBox>

        {/* Right Column: Image Collage */}
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          sx={{
            position: "relative",
            height: { 
              xs: "380px", // Mobile: Reduced for better fit
              sm: "450px", // Tablet: Reduced for better fit
              md: "480px", // Desktop: Keep as is
              lg: "500px" // Desktop: Keep as is
            },
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: { 
              xs: "24px", // Mobile: Add top padding
              sm: "32px", // Tablet: Add top padding
              md: "0px" // Desktop: No extra padding
            },
          }}
        >
          {/* Decorative Floating Shapes */}
          {/* Shape 1: -top-4 left-1/4 h-16 w-16 rounded-full bg-blue-200/50 */}
          <MotionBox
            variants={floatingVariants}
            animate="animate"
            sx={{
              position: "absolute",
              top: { xs: "-12px", sm: "-16px", md: "-16px" },
              left: { xs: "15%", sm: "18%", md: "20%" },
              width: { xs: "40px", sm: "48px", md: "56px" },
              height: { xs: "40px", sm: "48px", md: "56px" },
              borderRadius: "50%",
              backgroundColor: "rgba(191, 219, 254, 0.5)",
              zIndex: 1,
            }}
          />
          {/* Shape 2: bottom-0 right-1/4 h-12 w-12 rounded-lg bg-purple-200/50 */}
          <MotionBox
            variants={floatingVariants}
            animate="animate"
            sx={{
              position: "absolute",
              bottom: { xs: "8px", sm: "8px", md: "8px" },
              right: { xs: "15%", sm: "18%", md: "20%" },
              width: { xs: "32px", sm: "36px", md: "40px" },
              height: { xs: "32px", sm: "36px", md: "40px" },
              borderRadius: "8px",
              backgroundColor: "rgba(233, 213, 255, 0.5)",
              zIndex: 1,
            }}
            transition={{ delay: 0.5 }}
          />
          {/* Shape 3: bottom-1/4 left-4 h-6 w-6 rounded-full bg-green-200/50 */}
          <MotionBox
            variants={floatingVariants}
            animate="animate"
            sx={{
              position: "absolute",
              bottom: { xs: "25%", sm: "28%", md: "30%" },
              left: { xs: "8px", sm: "10px", md: "12px" },
              width: { xs: "16px", sm: "18px", md: "20px" },
              height: { xs: "16px", sm: "18px", md: "20px" },
              borderRadius: "50%",
              backgroundColor: "rgba(187, 247, 208, 0.5)",
              zIndex: 1,
            }}
            transition={{ delay: 1 }}
          />

          {/* Image 1 - Different layouts for mobile/tablet vs desktop */}
          <MotionBox
            variants={imageVariants}
            sx={{
              position: "absolute",
              // Mobile & Tablet: Better centered positioning
              left: { 
                xs: "50%", // Mobile: Center
                sm: "50%", // Tablet: Center
                md: "25%", // Desktop: Keep as is
              },
              // Mobile & Tablet: Center transform
              transform: { 
                xs: "translateX(-50%)", // Mobile: Center
                sm: "translateX(-50%)", // Tablet: Center
                md: "translateX(-50%)", // Desktop: Keep as is
              },
              top: { 
                xs: "-50px", // Mobile: Better top spacing
                sm: "-50px", // Tablet: Better top spacing
                md: "-20px", // Desktop: Keep as is
              },
              width: { 
                xs: "180px", // Mobile: Slightly larger
                sm: "240px", // Tablet: Slightly larger
                md: "256px", // Desktop: Keep as is
                lg: "290px" // Desktop: Keep as is
              },
              height: { 
                xs: "180px", // Mobile: Slightly larger
                sm: "240px", // Tablet: Slightly larger
                md: "256px", // Desktop: Keep as is
                lg: "260px" // Desktop: Keep as is
              },
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              zIndex: 2,
              backgroundColor: "#f1f5f9",
              padding: { 
                xs: "4px", // Mobile: Less padding for better highlight
                sm: "6px", // Tablet: Less padding
                md: "8px" // Desktop: Keep as is
              },
            }}
          >
            <Box
              component="img"
              src={images[0]}
              alt="Doctor with digital device"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://placehold.co/400x400/3567D6/ffffff?text=Healthcare+Professional";
              }}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "12px",
              }}
            />
          </MotionBox>

          {/* Image 2 - Different layouts for mobile/tablet vs desktop */}
          <MotionBox
            variants={imageVariants}
            sx={{
              position: "absolute",
              // Mobile & Tablet: Better positioning
              right: { 
                xs: "55%", // Mobile: Center from right
                sm: "55%", // Tablet: Center from right
                md: "16px", // Desktop: Keep as is
                lg: "0px" // Desktop: Keep as is
              },
              // Mobile & Tablet: Center transform
              transform: { 
                xs: "translateX(50%)", // Mobile: Center from right
                sm: "translateX(50%)", // Tablet: Center from right
                md: "none", // Desktop: No transform
              },
              top: { 
                xs: "15%", // Mobile: Better vertical positioning
                sm: "18%", // Tablet: Better vertical positioning
                md: "38%", // Desktop: Keep as is
                lg: "50%" // Desktop: Keep as is
              },
              width: { 
                xs: "160px", // Mobile: Slightly larger
                sm: "210px", // Tablet: Slightly larger
                md: "224px", // Desktop: Keep as is
                lg: "230px" // Desktop: Keep as is
              },
              height: { 
                xs: "160px", // Mobile: Slightly larger
                sm: "210px", // Tablet: Slightly larger
                md: "224px", // Desktop: Keep as is
                lg: "230px" // Desktop: Keep as is
              },
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              zIndex: 2,
              backgroundColor: "#f1f5f9",
              padding: { 
                xs: "4px", // Mobile: Less padding for better highlight
                sm: "6px", // Tablet: Less padding
                md: "8px" // Desktop: Keep as is
              },
            }}
          >
            <Box
              component="img"
              src={images[1]}
              alt="Patient viewing report"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://placehold.co/400x400/3567D6/ffffff?text=Patient+Viewing+Report";
              }}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "12px",
              }}
            />
          </MotionBox>

          {/* Image 3 - Different layouts for mobile/tablet vs desktop */}
          <MotionBox
            variants={imageVariants}
            sx={{
              position: "absolute",
              bottom: { 
                xs: "64px", // Mobile: Better bottom spacing
                sm: "54px", // Tablet: Better bottom spacing
                md: "16px", // Desktop: Keep as is
              },
              // Mobile & Tablet: Center
              left: { 
                xs: "50%", 
                sm: "50%", 
                md: "16px", // Desktop: Keep as is
              },
              // Mobile & Tablet: Center transform
              transform: { 
                xs: "translateX(-50%)", 
                sm: "translateX(-50%)", 
                md: "none", // Desktop: Keep as is
              },
              width: { 
                xs: "150px", // Mobile: Slightly larger
                sm: "200px", // Tablet: Slightly larger
                md: "192px", // Desktop: Keep as is
                lg: "200px" // Desktop: Keep as is
              },
              height: { 
                xs: "150px", // Mobile: Slightly larger
                sm: "200px", // Tablet: Slightly larger
                md: "192px", // Desktop: Keep as is
                lg: "200px" // Desktop: Keep as is
              },
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              zIndex: 2,
              backgroundColor: "#f1f5f9",
              padding: { 
                xs: "4px", // Mobile: Less padding for better highlight
                sm: "6px", // Tablet: Less padding
                md: "8px" // Desktop: Keep as is
              },
            }}
          >
            <Box
              component="img"
              src={images[2]}
              alt="Smart Report preview"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://placehold.co/400x400/3567D6/ffffff?text=Smart+Report";
              }}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "top center",
                borderRadius: "12px",
              }}
            />
          </MotionBox>
        </MotionBox>
      </Box>

      <MeetingModal showMeeting={showMeeting} setShowMeeting={setShowMeeting} />
    </Box>
  );
};

export default HeroSection;

